<script lang="ts" setup>
// DataCompletudeLegend (DEV-1650) — la légende COMMUNE des états d'un jour.
//
// Quatre entrées au plus, un mot chacune, une couleur ou un motif chacune
// (décision de Mathieu du 08/09). Elle est partagée par la barre de complétude et,
// plus tard, par les bandes dessinées dans les graphes : un seul vocabulaire, une
// seule palette, aucune phrase.
import { computed } from "vue"
import {
    ETATS_COMPLETUDE,
    ORDRE_LEGENDE,
    type EtatCompletude,
} from "@modules/data/utils/completude"

const props = withDefaults(
    defineProps<{
        /** États à montrer. Défaut : les quatre, dans l'ordre de la légende. */
        etats?: EtatCompletude[]
        /** Comptages par état, pour suffixer chaque mot du nombre de jours. */
        parEtat?: Record<EtatCompletude, number> | null
        /**
         * Les mots de la légende passent au texte courant au lieu du gris
         * estompé. Demandé par la page « Données du compteur », où `axe-core`
         * relève le gris du préréglage (#9e9e9e, 2,68:1 sur blanc) sous le
         * seuil AA de 4,5:1.
         *
         * DÉFAUT FAUX, ET C'EST LE POINT : la bande est montée aussi par la
         * page de données générique, qui vit HORS de l'interrupteur
         * `ENEDIS_PAGE_DONNEES_V2`. Un changement de couleur non gardé s'y
         * serait appliqué en production, interrupteur désarmé compris, ce que
         * l'engagement E11 interdit. Le gris de cette page reste donc ce qu'il
         * était ; son relèvement est un sujet à lui, à instruire à part.
         */
        motsContrastes?: boolean
    }>(),
    { etats: () => ORDRE_LEGENDE, parEtat: null, motsContrastes: false }
)

const montres = computed<EtatCompletude[]>(() =>
    props.etats.filter((etat) => etat in ETATS_COMPLETUDE)
)

function descripteur(etat: EtatCompletude) {
    return ETATS_COMPLETUDE[etat]
}

function nombre(etat: EtatCompletude): number | null {
    return props.parEtat ? (props.parEtat[etat] ?? null) : null
}
</script>

<template>
    <ul
        class="data-completude-legend"
        :class="{
            'data-completude-legend--mots-contrastes': motsContrastes,
        }"
        aria-label="Légende des états de la donnée"
    >
        <li
            v-for="etat in montres"
            :key="etat"
            class="data-completude-legend__item"
        >
            <span
                class="data-completude-legend__puce"
                :class="{
                    'data-completude-legend__puce--hachures':
                        descripteur(etat).motif === 'hachures',
                }"
                :style="{
                    '--puce-fond': descripteur(etat).fond,
                    '--puce-trait': descripteur(etat).trait,
                }"
                aria-hidden="true"
            ></span>
            <span class="data-completude-legend__mot">
                {{ descripteur(etat).mot }}
            </span>
            <span
                v-if="nombre(etat) !== null"
                class="data-completude-legend__nombre"
            >
                {{ nombre(etat) }}
            </span>
        </li>
    </ul>
</template>

<style scoped>
.data-completude-legend {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 16px;
    margin: 0;
    padding: 0;
    list-style: none;
}

.data-completude-legend__item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    line-height: 1.4;
    white-space: nowrap;
}

.data-completude-legend__puce {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 3px;
    background-color: var(--puce-fond);
    border: 1px solid var(--puce-trait);
}

/* Le MOTIF porte l'absence : hachures à 45°, lisibles en noir et blanc et sans
   dépendre de la seule couleur (accessibilité). */
.data-completude-legend__puce--hachures {
    background-image: repeating-linear-gradient(
        45deg,
        var(--puce-trait) 0,
        var(--puce-trait) 1.5px,
        transparent 1.5px,
        transparent 4px
    );
}

.data-completude-legend__mot {
    color: var(--p-text-muted-color, #64748b);
    font-weight: 500;
}

/* Le gris estompé du préréglage vaut #9e9e9e, soit 2,68:1 sur blanc : sous le
   seuil AA de 4,5:1, et `axe-core` le relève sur les deux récits du J5. Les
   quatre mots passent alors au texte courant (#616161, 6,19:1), la hiérarchie
   restant tenue par la taille. Sur demande seulement : voir `motsContrastes`. */
.data-completude-legend--mots-contrastes .data-completude-legend__mot {
    color: var(--p-text-color, #2d2e32);
}

.data-completude-legend__nombre {
    color: var(--p-text-color, #2d2e32);
    font-variant-numeric: tabular-nums;
}
</style>
